import { useCallback, useMemo } from "react";

import { NetworkStatus, useQuery } from "@apollo/client";
import { type ApolloQueryResult } from "@apollo/client/core/types";
import { useRefract } from "refract-rxjs";

import { usePagination } from "../../core/pagination/hooks";
import { type PaginationFilter } from "../../core/pagination/models";
import { GetLensesType, type MyLensesLens } from "../../gql-generated/types";
import { type PaginationAtomsKeys } from "../../shared/state";
import { toPaginationRequest } from "../api/mappers";
import {
    type GetLensesListQuery,
    type GetLensesListQueryVariables,
    getLensesForUpdate,
    getLensesList,
} from "../api/queries";
import { type NoLensesOnInitProps, isFirstFetchingAperture, noLensesOnInitAperture } from "../effects";

import { useLensesDebouncedSearch } from "./useLensesDebouncedSearch";

export interface LensesOptions {
    filterByOptions?: Partial<PaginationFilter>;
    lensSpaceId?: string;
    paginationAtomsKey?: PaginationAtomsKeys;
    forUpdate?: boolean;
    skip?: boolean;
}

export const useLenses = ({
    filterByOptions: filterByInitialOptions,
    lensSpaceId,
    paginationAtomsKey,
    forUpdate = false,
    skip = false,
}: LensesOptions): {
    loadMore: () => Promise<ApolloQueryResult<GetLensesListQuery>>;
    lenses: readonly MyLensesLens[];
    hasMore: boolean;
    filterBy: PaginationFilter;
    setLensSearchText: (search: string) => void;
    lensSearchText: string;
    noLensesOnInit: boolean;
    isFirstFetching: boolean;
    isReFetching: boolean;
} => {
    const { pagination, filterBy, setSearch } = usePagination(filterByInitialOptions, paginationAtomsKey);
    const { lensSearchText, setLensSearchText } = useLensesDebouncedSearch(
        filterBy.searchName,
        setSearch,
        paginationAtomsKey,
    );

    const queryVariables = useMemo(
        () => toPaginationRequest(pagination, filterBy, GetLensesType.LensSpace, lensSpaceId),
        [lensSpaceId, pagination, filterBy],
    );

    const queryDocument = forUpdate ? getLensesForUpdate : getLensesList;

    const queryResult = useQuery<GetLensesListQuery, GetLensesListQueryVariables>(queryDocument, {
        notifyOnNetworkStatusChange: true,
        variables: queryVariables,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
        skip: skip || !lensSpaceId,
    });
    const { data, loading, networkStatus } = queryResult;

    const lenses = data?.lenses.lensesList ?? [];
    const hasMore = lenses.length >= pagination.limit && lenses.length % pagination.limit === 0;

    const loadMore = useCallback(() => {
        return queryResult.fetchMore({
            variables: { offset: lenses.length },
        });
    }, [queryResult, lenses.length]);

    const isFetchingMore = networkStatus === NetworkStatus.fetchMore || networkStatus === NetworkStatus.poll;
    const isReFetching = isFetchingMore ? false : loading;

    const noLensesOnInit = useRefract<NoLensesOnInitProps, boolean>(noLensesOnInitAperture, {
        data,
        loading,
    });

    const isFirstFetching = useRefract<{ data: GetLensesListQuery | undefined }, boolean>(isFirstFetchingAperture, {
        data,
    });

    return {
        loadMore,
        lenses,
        hasMore,
        filterBy,
        lensSearchText,
        setLensSearchText,
        noLensesOnInit,
        isFirstFetching,
        isReFetching,
    };
};
